import { fetchEventsWithMetadata } from '../api/events';
import { validCalendlyLink, requestOwner } from './model';

// Explicit development-only preview. No authentication bypass or demo data in production.
export const LESSONS_DEMO = process.env.NODE_ENV === 'development'
  && process.env.REACT_APP_LESSONS_DEMO === 'true'
  && ['localhost', '127.0.0.1'].includes(window.location.hostname);
const KEY = 'ken-bryn-lesson-preview-v1';
const upcoming = () => {
  const start = new Date(); start.setUTCDate(start.getUTCDate() + 21);
  const end = new Date(start); end.setUTCDate(end.getUTCDate() + 2);
  return [{ id: 'preview-weekend', name: 'Example Dance Weekend (preview)', startDate: start.toISOString().slice(0, 10), endDate: end.toISOString().slice(0, 10), location: 'Chicago, IL' }];
};
const read = () => {
  const data = JSON.parse(localStorage.getItem(KEY) || '{"requests":[],"settings":[],"history":[]}');
  data.requests = data.requests.map(row => ['waiting', 'ready', 'contacted'].includes(row.status) || (row.kind === 'local' && row.status === 'invited') ? { ...row, status: 'new' } : row.status === 'booked' ? { ...row, status: 'closed', closed_reason: 'Previously recorded as booked; booking management is now in Calendly.' } : row);
  data.requests = data.requests.map(row => ({ ...row, assignee: requestOwner(row.instructor, row.assignee) }));
  return data;
};
const write = data => localStorage.setItem(KEY, JSON.stringify(data));
export const demoStore = {
  events: async () => {
    let result;
    try { result = process.env.REACT_APP_LESSONS_PREVIEW_EVENTS ? JSON.parse(process.env.REACT_APP_LESSONS_PREVIEW_EVENTS) : await fetchEventsWithMetadata(); }
    catch { result = { events: upcoming(), stale: false, previewFallback: true }; }
    const data = read(); data.eventList = result.events; write(data);
    return result;
  },
  load: async () => ({ ...read(), notifications: [] }),
  submit: async (values, admin) => {
    const data = read();
    if (data.requests.some(row => row.submission_key === values.submission_key)) return { ok: true };
    const event = (data.eventList || upcoming()).find(event => event.id === values.event_id);
    if (values.kind === 'event' && !event) throw new Error('Choose an available event.');
    const now = new Date().toISOString();
    const { hcaptchaToken, website, ...fields } = values;
    const row = { ...fields, id: crypto.randomUUID(), event_key: event && values.kind === 'event' ? `event:${event.id}` : 'local', event_name: values.kind === 'event' ? event.name : 'Local lessons', event_start: values.kind === 'event' ? event.startDate : null, event_end: values.kind === 'event' ? event.endDate : null, event_location: values.kind === 'event' ? event.location : '', status: 'new', notes: '', assignee: '', closed_reason: '', last_contacted_at: null, follow_up_on: null, source: admin ? values.source : 'website', created_at: now, updated_at: now };
    data.requests.unshift(row);
    row.assignee = requestOwner(row.instructor);
    data.history.unshift({ request_id: row.id, action: 'Request received', changes: {}, created_at: now });
    write(data); return { ok: true };
  },
  update: async (request, changes) => {
    const data = read();
    const index = data.requests.findIndex(row => row.id === request.id);
    if (index < 0 || data.requests[index].updated_at !== request.updated_at) throw new Error('This request changed. Refresh the list.');
    const now = new Date().toISOString();
    changes = { ...changes, assignee: requestOwner(changes.instructor ?? data.requests[index].instructor, changes.assignee ?? data.requests[index].assignee) };
    const changed = Object.fromEntries(Object.entries(changes).filter(([key, value]) => JSON.stringify(value) !== JSON.stringify(data.requests[index][key])).map(([key, value]) => [key, { from: data.requests[index][key], to: value }]));
    data.requests[index] = { ...data.requests[index], ...changes, updated_at: now };
    data.history.unshift({ request_id: request.id, action: 'Request updated', changes: changed, created_at: now });
    write(data); return data.requests[index];
  },
  settings: async (settings, expectedUpdatedAt = null) => {
    const data = read();
    if (!settings.event_key?.startsWith('event:')) throw new Error('Calendly links are only used for event lessons.');
    const existing = data.settings.find(row => row.event_key === settings.event_key);
    if ((existing?.updated_at || null) !== expectedUpdatedAt) throw new Error('Booking settings changed elsewhere. Refresh the list before saving again.');
    for (const host of ['ken', 'bryn']) {
      const link = (settings[`calendly_${host}`] || '').trim();
      if (!validCalendlyLink(link)) throw new Error('Use a valid https://calendly.com/ booking page.');
      settings = { ...settings, [`calendly_${host}`]: link, [`${host}_booking_open`]: Boolean(link) };
    }
    const now = new Date().toISOString();
    data.settings = data.settings.filter(row => row.event_key !== settings.event_key);
    data.settings.push({ ...existing, ...settings, updated_at: now });
    write(data); return data.settings;
  },
  history: id => read().history.filter(row => row.request_id === id),
};
